import React from "react";
import { formatOrderedTime, formatOrderType } from "./utils/orderUtils";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import api from "../../utils/api";
import { toast } from "react-toastify";

const OrderViewModal = ({ order, onClose }) => {
    const { locale, taxLabel, timezone } = useRestaurantConfig();
    const currencySymbol = getCurrencySymbol(locale);

    if (!order) return null;

    //  FILTER ONLY ACTIVE PRODUCTS
    const products = (order.products || []).filter(
        (p) => p.active !== false
    );


    let taxrate = Number(order.taxrate ?? 0);

    // convert 5 → 0.05
    if (taxrate > 1) {
        taxrate = taxrate / 100;
    }

    if (isNaN(taxrate)) taxrate = 0;

    const subTotal = Number(order.subTotal ?? 0);
    const gstAmount = Number(order.taxAmount ?? 0);
    const totalPackageCharge = Number(order.packagingTotal ?? 0);
    const totalWithGST = Number(order.amount ?? 0);

    const totalItems = products.reduce(
        (sum, p) => sum + Number(p.quantity || 0),
        0
    );
    const handleReprint = async () => {
        try {
            await api.post(`/orders/reprint-customer/${order.orderId}`);
            toast.success("Customer copy sent to printer");
        } catch (err) {
            console.error(err);
            toast.error("Failed to reprint");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white w-[600px] max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl p-6 relative">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-red-500 text-xl"
                >
                    ✕
                </button>

                {/* Header */}
                <h2 className="text-xl font-bold mb-4 border-b pb-2">
                    Order Details
                </h2>

                {/* Basic Info */}
                <div className="space-y-2 text-sm mb-4">
                    <p><strong>Bill Number:</strong> {order.billNumber || "-"}</p>
                    <p><strong>Order ID:</strong> #{order.orderId}</p>
                    <p><strong>Name:</strong> {order.name || "Guest"}</p>
                    <p><strong>Mobile:</strong> {order.phoneNumber || "-"}</p>
                    <p><strong>Order Type:</strong> {formatOrderType(order.orderType)}</p>
                    <p><strong>Status:</strong> {order.status}</p>
                    <p><strong>Order Time:</strong> {formatOrderedTime(order.createdAt, timezone)}</p>
                    {order?.table ? (
                        <p>
                            <strong>Table:</strong> {order.table}
                        </p>
                    ) : null}


                    {order.address && (
                        <p>
                            <strong>Delivery Address:</strong>{" "}
                            {`${order.address.replace(/,+\s*$/, "")}${order.pincode ? `, ${order.pincode}` : ""}`}
                        </p>
                    )}

                    {order.assignedDriver && (
                        <p>
                            <strong>Driver Details:</strong>{" "}
                            {order.assignedDriver.name} - {order.assignedDriver.mobile}
                        </p>
                    )}
                </div>

                {/* Products */}
                {products.length > 0 && (
                    <div>
                        <p className="font-semibold mb-2">Ordered Items</p>

                        <table className="w-full text-sm border">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="p-2 text-left">Product</th>
                                    <th className="p-2 text-center">Qty</th>
                                    <th className="p-2 text-right">Price</th>
                                    <th className="p-2 text-right">Total</th>
                                </tr>
                            </thead>

                            <tbody>
                                {products.map((item, index) => {
                                    const unitPrice = Number(item.price || 0);
                                    const lineTotal = Number(item.subTotal ?? unitPrice * item.quantity);

                                    return (
                                        <tr key={index} className="border-t">
                                            <td className="p-2">
                                                {item.productTitle?.split(",")[0] || "Unnamed"}
                                            </td>

                                            <td className="p-2 text-center">
                                                {item.quantity}
                                            </td>

                                            <td className="p-2 text-right">
                                                {currencySymbol}
                                                {unitPrice.toFixed(2)}
                                            </td>

                                            <td className="p-2 text-right font-medium">
                                                {currencySymbol}
                                                {lineTotal.toFixed(2)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Totals */}
                {products.length > 0 && (
                    <div className="mt-4 border-t pt-3 flex justify-between items-start">
                        <div>
                            <span className="text-sm text-gray-700">
                                Total Items:{" "}
                            </span>
                            <strong>{totalItems}</strong>
                        </div>

                        <div className="text-right">
                            <div className="text-sm">
                                Subtotal:{" "}
                                <strong>
                                    {currencySymbol}
                                    {subTotal.toFixed(2)}
                                </strong>
                            </div>

                            <div className="text-sm">
                                {taxLabel} ({(taxrate * 100).toFixed(0)}%):{" "}
                                <strong>
                                    {currencySymbol}
                                    {gstAmount.toFixed(2)}
                                </strong>
                            </div>

                            {totalPackageCharge > 0 && (
                                <div className="text-sm">
                                    Packaging Charges:{" "}
                                    <strong>
                                        {currencySymbol}
                                        {totalPackageCharge.toFixed(2)}
                                    </strong>
                                </div>
                            )}

                            <div className="text-sm mt-1">
                                Total Price:{" "}
                                <strong className="text-lg text-green-600">
                                    {currencySymbol}
                                    {totalWithGST.toFixed(2)}
                                </strong>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={handleReprint}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium shadow"
                    >
                        Reprint
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderViewModal;